<template>
  <UPageSection :title="resolvedTitle" :description="resolvedDescription" :dir="useDir().value" :ui="{
    container: 'px-4 sm:px-6 !pt-0 gap-4 sm:gap-4',
    title: ' text-xl sm:text-xl lg:text-2xl font-medium',
    description: ' mt-2 text-sm sm:text-md lg:text-sm text-muted'
  }">
    <UTabs
      :activation-mode="'automatic'"
      :items="tabItems"
      :default-value="firstTabValue"
      orientation="horizontal"
      :dir="useDir().value"
      :ui="tabsUi"
    >
      <template #content="{ item }">
        <UAccordion class="mx-1" :dir="useDir().value" trailing-icon="lucide:plus" :items="item.questions"
          :unmount-on-hide="false" :ui="accordionUi" />
      </template>
    </UTabs>
  </UPageSection>
</template>

<script setup lang="ts">
export interface FaqQuestion {
  label: string
  content: string
}

export interface FaqCategory {
  title: string
  questions: FaqQuestion[]
}

const props = withDefaults(
  defineProps<{
    title?: string | Record<string, string>
    description?: string | Record<string, string>
    categories?: Array<{
      title?: string | Record<string, string>
      questions?: Array<{
        label?: string | Record<string, string>
        content?: string | Record<string, string>
      }>
    }>
  }>(),
  {
    categories: () => [],
  },
)

const pageLocale = useState<string>('page-locale', () => 'fa')

function resolveVal(value: unknown, loc: string): any {
  if (value == null) return ''
  if (Array.isArray(value)) return value.map((item) => resolveVal(item, loc))
  if (typeof value === 'object') {
    const rec = value as Record<string, unknown>
    if (rec[loc] !== undefined) return rec[loc]
    return Object.fromEntries(
      Object.entries(rec).map(([k, v]) => [k, resolveVal(v, loc)]),
    )
  }
  return value
}

const resolvedTitle = computed(() => {
  const t = props.title
  if (typeof t === 'string') return t
  return t ? String(resolveVal(t, pageLocale.value)) : ''
})

const resolvedDescription = computed(() => {
  const d = props.description
  if (typeof d === 'string') return d
  return d ? String(resolveVal(d, pageLocale.value)) : ''
})

const defaultCategories = [
  {
    title: { en: 'General', fa: 'عمومی' },
    questions: [
      {
        label: { en: 'What is this site built with?', fa: 'این سایت با چه چیزی ساخته شده؟' },
        content: { en: 'Nuxt 4, Vue 3, Tailwind and Nuxt UI.', fa: 'Nuxt 4، Vue 3، Tailwind و Nuxt UI.' },
      },
      {
        label: { en: 'Can I add custom blocks?', fa: 'می‌توانم بلاک سفارشی اضافه کنم؟' },
        content: { en: 'Yes. Add components in components/blocks/ and reference them in your JSON.', fa: 'بله. کامپوننت‌ها را در components/blocks/ اضافه کنید و در JSON استفاده کنید.' },
      },
      {
        label: { en: 'Is the content editable?', fa: 'محتوا قابل ویرایش است؟' },
        content: { en: 'Yes. Edit content/index.json or use the builder page.', fa: 'بله. content/index.json را ویرایش کنید یا از صفحه بیلدر استفاده کنید.' },
      },
    ],
  },
]

const resolvedCategories = computed(() => {
  const raw = props.categories ?? []
  const list = raw.length
    ? raw.map((cat) => ({
      title: typeof cat.title === 'string' ? cat.title : (resolveVal(cat.title, pageLocale.value) as string),
      questions: (cat.questions ?? []).map((q) => ({
        label: typeof q.label === 'string' ? q.label : (resolveVal(q.label, pageLocale.value) as string),
        content: typeof q.content === 'string' ? q.content : (resolveVal(q.content, pageLocale.value) as string),
      })),
    }))
    : defaultCategories.map((cat) => ({
      title: resolveVal(cat.title, pageLocale.value) as string,
      questions: cat.questions.map((q) => ({
        label: resolveVal(q.label, pageLocale.value) as string,
        content: resolveVal(q.content, pageLocale.value) as string,
      })),
    }))
  return list
})

const tabItems = computed(() =>
  (resolvedCategories.value ?? []).map((cat, index) => ({
    label: cat.title ?? '',
    value: (cat.title || '').toLowerCase().replace(/\s+/g, '-') || String(index),
    key: (cat.title || '').toLowerCase().replace(/\s+/g, '-') || String(index),
    questions: (cat.questions ?? []).map((q, qi) => ({
      label: q.label ?? '',
      content: q.content ?? '',
      value: `q-${index}-${qi}`,
    })),
  })),
)

const firstTabValue = computed(() => tabItems.value[0]?.value ?? '0')

const tabsUi = {
  root: 'flex items-center gap-4 w-full',
  list: 'relative flex bg-transparent dark:bg-transparent gap-2 px-0',
  indicator:
    'absolute top-[4px] duration-200 ease-out focus:outline-none rounded-lg bg-elevated/60',
  trigger:
    'px-3 py-2 rounded-lg hover:bg-muted/50 data-[state=active]:text-highlighted data-[state=inactive]:text-muted',
  label: 'truncate',
}

const accordionUi = {
  item: 'border-none',
  trigger:
    'mb-2 border-0 group px-4 transform-gpu rounded-lg bg-elevated/60 will-change-transform hover:bg-muted/50 text-base',
  trailingIcon:
    'group-data-[state=closed]:rotate-0 group-data-[state=open]:rotate-135 text-base text-muted',
}
</script>
